import React, { useContext, useMemo, useRef } from "react";
import { AssetWithState } from "../../AssetList/AssetList";
import { Alert, Box, Grid, MenuItem, Typography } from "@mui/material";
import {
  formTitleStyles,
  locationStyle,
} from "../component/DeliveryInformationForm/UserDetailInfo/UserDetailInfo";
import CancelButton from "../component/CancelButton/CancelButton";
import BackButton from "../component/BackButton/BackButton";
import CloseIconButton from "../component/CloseIconButton/CloseIconButton";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import CustomerSelect from "../component/CustomerSelect/CustomerSelect";
import ConformButton from "../component/ConfirmButton/ConformButton";
import ReasonInput from "../component/ReasonInput/ReasonInput";
import { UseUpdateReplacementRequest } from "../async/useUpdateReplacementRequest";
import { DeferReplacementRequestBody } from "../../../resource/bff";
import { AssetReplacementListContext } from "../../HardwareDetails/HardwareDetails";
import {
  DeferReplacementReasonEnum,
  RequestStatusEnum,
} from "../../../helper/constant";
import moment from "moment/moment";

type Props = {
  currentAsset: AssetWithState;
  handleCloseModal: () => void;
  changeDeferReplacementModal: (val: boolean) => void;
  setAlertMessageOpen: () => void;
  setIsAlertStatus: (val: RequestStatusEnum) => void;
  setLoadingStatus: (val: boolean) => void;
};

export type DeferReplacementFormValue = {
  reason: string;
  input?: string;
};

const DeferReplacementForm = ({
  currentAsset,
  handleCloseModal,
  changeDeferReplacementModal,
  setIsAlertStatus,
  setAlertMessageOpen,
  setLoadingStatus,
}: Props) => {
  const {
    getValues,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<DeferReplacementFormValue>({
    defaultValues: {
      reason: null,
      input: "",
    },
  });

  const { mutate } = UseUpdateReplacementRequest();
  const { refetchAssetReplacements } = useContext(AssetReplacementListContext);

  const selectRef = useRef<HTMLDivElement>(null);

  const formatDeferDate = useMemo(() => {
    const timestamp = Number(currentAsset.initiatedDate);
    const deferMonth = Number(currentAsset.replacementPeriod);
    const date = new Date(timestamp);
    date.setMonth(date.getMonth() + deferMonth);
    return moment(date).format("MMM DD, yyyy");
  }, [currentAsset.initiatedDate, currentAsset.replacementPeriod]);

  const isReasonInputShow = useMemo(
    () => Boolean(getValues("reason") === DeferReplacementReasonEnum.OTHER),
    [watch("reason")]
  );

  const onSubmit: SubmitHandler<DeferReplacementFormValue> = (data) => {
    setLoadingStatus(true);
    const buildRequestBody: DeferReplacementRequestBody = {
      requestId: currentAsset.requestId,
      reason:
        data.input.length > 0 && isReasonInputShow
          ? `${data.reason}: ${data.input}|`
          : `${data.reason}`,
    };
    mutate(buildRequestBody, {
      onSuccess: (data) => {
        refetchAssetReplacements().then(() => {
          setLoadingStatus(false);
          handleCloseModal();
        });
        setIsAlertStatus(RequestStatusEnum.REQUEST_SUCCESS);
        setAlertMessageOpen();
      },
      onError: (error) => {
        setLoadingStatus(false);
        setIsAlertStatus(RequestStatusEnum.FAILURE);
        setAlertMessageOpen();
      },
      onSettled: () => {},
    });
  };

  const defermentReason = Object.values(DeferReplacementReasonEnum);

  return (
    <>
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography
            id="defer-replacement-modal-title"
            variant="h6"
            component="h2"
            color={"#634F7D"}
            fontFamily={"Inter"}
            fontWeight={"700"}
            fontSize={"24px"}
          >
            Defer Replacement
          </Typography>
          <Box sx={{ display: "flex", gap: "24px", alignItems: "center" }}>
            <CloseIconButton
              handleClick={() => {
                handleCloseModal();
              }}
            />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} sx={{ marginBottom: "16px" }}>
        <Alert severity="warning" variant="outlined">
          You can defer replacement only one time. You must replace your device
          in the next cycle.
        </Alert>
      </Grid>
      <Box sx={{ width: "100%" }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12}>
                <Box
                  sx={{
                    padding: "16px",
                    position: "relative",
                  }}
                >
                  <Box sx={{ marginBottom: "24px" }}>
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={formTitleStyles}
                      marginBottom={"12px"}
                    >
                      Defer Period
                    </Typography>
                    <Box>
                      <p style={locationStyle}>
                        Defer the laptop replacement for next cycle starting on
                        <Typography component="span" fontWeight={"700"}>
                          {" "}
                          {formatDeferDate}
                        </Typography>
                      </p>
                    </Box>
                  </Box>
                  <Box sx={{ marginBottom: "24px" }}>
                    <Controller
                      rules={{
                        required: true,
                      }}
                      name="reason"
                      control={control}
                      render={({ field }) => (
                        <CustomerSelect
                          ref={selectRef}
                          {...field}
                          variant={"standard"}
                          sx={{ width: "50%" }}
                          inputLabel="Reason for deferment"
                        >
                          {defermentReason.map((val) => (
                            <MenuItem value={val} key={val}>
                              {val}
                            </MenuItem>
                          ))}
                        </CustomerSelect>
                      )}
                    />
                  </Box>
                  {isReasonInputShow && (
                    <Box sx={{ width: "100%" }}>
                      <ReasonInput control={control} error={!isValid} />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="flex-end"
            gap="32px"
            sx={{
              marginTop: "48px",
            }}
          >
            <CancelButton
              handleClick={() => {
                handleCloseModal();
              }}
            />
            <BackButton
              handleClick={() => {
                changeDeferReplacementModal(false);
              }}
            />
            <ConformButton
              text={"Confirm Deferment"}
              disabled={!isValid}
              handleClick={() => {}}
            />
          </Grid>
        </form>
      </Box>
    </>
  );
};

export default DeferReplacementForm;
